import * as React from 'react';
import {
    FilterList,
    FilterListItem,
    FilterButton,
    FilterForm,
    SearchInput,
    DateInput,
    Button,
    useListContext,
    SelectArrayInput,
    ReferenceArrayInput,
    AutocompleteArrayInput,
} from 'react-admin';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import {
    startOfWeek,
    startOfMonth,
    subMonths,
    startOfToday,
    addWeeks,
    addMonths,
    endOfToday,
    endOfWeek,
    endOfMonth,
    subWeeks,
} from 'date-fns';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { ActivityIcon } from '@app/activity/ActivityIcon';
import { useMissionTypes } from '@app/mission_type/useMissionType';
import {
    antennaInputText,
    antennaOptionText,
} from '@app/antenna/AntennaInputOption';
import { dateFilterInputProps } from '@core/utils/dateFilterInputProps';
import { MissionTypeChipFromId } from '../chips/MissionTypeChip';

export const MissionListFilter = ({
    disableSaveQuery,
    sx,
    showDateFilters = false,
    disableAntennaFilter = false,
}: {
    disableSaveQuery?: boolean;
    sx?: SxProps<Theme>;
    showDateFilters?: boolean;
    disableAntennaFilter?: boolean;
}) => {
    const { missionStatusLabels } = useConfigurationContext();
    const { data: missionTypes = [] } = useMissionTypes();

    const { setFilters, filterValues } = useListContext();

    const missionFilters = [
        <SearchInput
            source="search"
            alwaysOn
            placeholder="Search name, description, partner info, etc."
        />,
        <DateInput
            source="startDate.gte"
            label="Start date after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="startDate.lte"
            label="Start date before"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="endDate.gte"
            label="End date after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="endDate.lte"
            label="End date before"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="updatedAt.gte"
            label="Updated after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="updatedAt.lte"
            label="Updated before"
            {...dateFilterInputProps}
        />,
        <SelectArrayInput
            source="type.in"
            label="Type (or)"
            optionText={record => <MissionTypeChipFromId id={record.id} />}
            choices={missionTypes}
        />,
        <SelectArrayInput
            source="status.in"
            label="Status (or)"
            choices={missionStatusLabels.map(status => ({
                id: status.value,
                name: status.label,
            }))}
        />,
        !disableAntennaFilter && (
            <ReferenceArrayInput
                label="Antennas (or)"
                reference="antenna"
                source="antennaId.in"
                sort={{ field: 'name', order: 'ASC' }}
            >
                <AutocompleteArrayInput
                    label="Antennas (or)"
                    optionText={antennaOptionText}
                    inputText={antennaInputText}
                    helperText={false}
                    filterToQuery={search => ({ search })}
                />
            </ReferenceArrayInput>
        ),
    ].filter(Boolean);

    const hasFilters = filterValues && Object.keys(filterValues).length > 0;

    const handleClearFilters = () => {
        setFilters({});
    };

    return (
        <Box minWidth="10em" maxWidth="18em" order={-1} mr={2} sx={sx}>
            <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <FilterButton
                    filters={missionFilters}
                    disableSaveQuery={disableSaveQuery}
                />
                <Button
                    disabled={!hasFilters}
                    label="Clear all"
                    onClick={handleClearFilters}
                    variant="outlined"
                    size="small"
                />
            </Stack>
            <FilterForm filters={missionFilters} />

            {showDateFilters && (
                <>
                    <FilterList label="Starting" icon={<ActivityIcon />}>
                        <FilterListItem
                            label="Before last month"
                            value={{
                                startDate: {
                                    lte: endOfMonth(
                                        subMonths(new Date(), 1)
                                    ).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="This month"
                            value={{
                                startDate: {
                                    gte: startOfMonth(new Date()).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="Last week"
                            value={{
                                startDate: {
                                    gte: startOfWeek(
                                        subWeeks(new Date(), 1)
                                    ).toISOString(),
                                    lte: endOfWeek(
                                        subWeeks(new Date(), 1)
                                    ).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="Today"
                            value={{
                                startDate: {
                                    gte: startOfToday().toISOString(),
                                    lte: endOfToday().toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="This week"
                            value={{
                                startDate: {
                                    gte: startOfWeek(new Date()).toISOString(),
                                    lte: endOfWeek(new Date()).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="After this week"
                            value={{
                                startDate: {
                                    gte: startOfWeek(
                                        addWeeks(new Date(), 1)
                                    ).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="After this month"
                            value={{
                                startDate: {
                                    gte: startOfMonth(
                                        addMonths(new Date(), 1)
                                    ).toISOString(),
                                },
                            }}
                        />
                    </FilterList>

                    <FilterList label="Last activity" icon={<ActivityIcon />}>
                        <FilterListItem
                            label="Today"
                            value={{
                                updatedAt: {
                                    gte: startOfToday().toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="This week"
                            value={{
                                updatedAt: {
                                    gte: startOfWeek(new Date()).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="Before this week"
                            value={{
                                updatedAt: {
                                    lte: startOfWeek(new Date()).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="Before this month"
                            value={{
                                updatedAt: {
                                    lte: startOfMonth(new Date()).toISOString(),
                                },
                            }}
                        />
                        <FilterListItem
                            label="Before last month"
                            value={{
                                updatedAt: {
                                    lte: subMonths(
                                        startOfMonth(new Date()),
                                        1
                                    ).toISOString(),
                                },
                            }}
                        />
                    </FilterList>
                </>
            )}
        </Box>
    );
};
